<template>
  <section
    class="invoice-preview-wrapper"
    style="position: relative"
  >
    <!-- Alert: No items found (only show if not loading and no redirect in progress) -->
    <b-alert
      variant="info"
      :show="orders.length === 0 && !isLoading"
    >
      <h4 class="alert-heading">
        Aucun échange trouvé
      </h4>
      <div class="alert-body">
        Aucun colis d'échange n'a été trouvé pour cette référence.
      </div>
    </b-alert>

    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="text-center py-5"
    >
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="sr-only">Chargement...</span>
      </div>
      <p class="mt-2 text-muted">
        Chargement des échanges...
      </p>
    </div>

    <!-- Print All Button -->
    <b-row
      v-if="orders.length > 0"
      class="mb-3 no-print"
    >
      <b-col cols="12">
        <div class="d-flex justify-content-end">
          <b-button
            variant="primary"
            @click="printAll"
          >
            <feather-icon
              icon="PrinterIcon"
              class="mr-1"
            />
            Imprimer tous ({{ orders.length }})
          </b-button>
        </div>
      </b-col>
    </b-row>

    <!-- Orders Display -->
    <div
      v-for="(order, index) in orders"
      :key="order.id"
      class="order-section"
      :class="{ 'page-break': (index + 1) % 2 === 0 && index < orders.length - 1 }"
    >
      <b-row class="invoice-preview">
        <!-- Col: Left (Invoice Container) -->
        <b-col
          cols="12"
          xl="9"
          md="8"
        >
          <b-card
            no-body
            class="invoice-preview-card"
          >
            <div class="d-flex justify-content-between flex-wrap mr-1 ml-1">
              <div class="invoice-preview">
                <strong
                  class="mt-1 exchange-title"
                  style="color: #0A2634; font-size: 28px"
                >
                  Colis Echange
                </strong>
                <h3 style="color: black ">
                  Echange :
                  <span
                    class="invoice-date"
                    style="color: black"
                  >
                    {{ currentDateTime() }}</span>
                </h3>
                <h3 style="color: black ">
                  Date:
                  <span
                    class="invoice-date"
                    style="color: black"
                  >
                    {{ formatFn(order.created_at) }}</span>
                </h3>
                <div class="d-flex justify-content-between mt-1">
                  <div>
                    <qrcode-vue
                      :value="order.id.toString()"
                      :size="size"
                      level="H"
                    />
                    <span>{{ order.id }}</span>
                  </div>
                  <div>
                    <barcode
                      height="30"
                      width="2"
                      :value="order.id.toString()"
                    />
                  </div>
                </div>
              </div>

              <div style="text-align:end">
                <div>
                  <img
                    src="/logo.png"
                    width="250px"
                    height="100%"
                  >
                </div>
                <div class="mt-1">
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">{{ company.name }} :</strong> {{ company.tagline || 'Société de livraison des colis' }}
                  </b-card-text>
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">Matricule fiscale :</strong> {{ company.tax_identification_number }}
                  </b-card-text>
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">Adresse :</strong> {{ company.address }}
                  </b-card-text>
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">Tel :</strong> {{ company.phone1 }}
                  </b-card-text>
                </div>
              </div>
            </div>

            <!-- Exchange Status Indicator -->
            <b-card-text
              v-if="order.is_an_exchange === true"
              class="h4 ml-1 text-center p-1"
              style="color:black;font-size: 25px; border:1px solid black; background-color: #d4edda;"
            >
              <strong style="color:#0A2634">Échange</strong>
            </b-card-text>

            <!-- Non-Exchange Status Indicator -->
            <b-card-text
              v-if="order.is_an_exchange !== true"
              class="h4 ml-1 text-center p-1"
              style="color:black;font-size: 25px; border:1px solid #dc3545; background-color: #f8d7da;"
            >
              <strong style="color:#dc3545">Colis Standard (Non Échange)</strong>
            </b-card-text>

            <div class="d-flex flex-row justify-content-between mr-1 ml-1 mb-1">
              <div>
                <div style="border: 2px solid black;font-size: 18px;">
                  <b-card-text class="ml-2 mr-2 mt-2 mb-1 font-weight-bold">
                    <span style="font-weight:900; color: #0A2634">Client : </span> {{ order.name }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634">Gouvernorat : </span>{{ order.governorate.name }} <span v-if="order.delegation != null"> {{ order.delegation.name }}</span>
                  </b-card-text>

                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634">Adresse :</span>
                    {{ order.address }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634"> téléphone :</span>
                    {{ order.phone1 }} / {{ order.phone2 }}
                  </b-card-text>
                </div>
              </div>
              <div>
                <div
                  class="ml-2"
                  style="border: 2px solid black;font-size: 18px;"
                >
                  <b-card-text class="ml-2 mr-2 mt-2 mb-1 font-weight-bold">
                    <span style="font-weight:900; color: #0A2634">Fournisseur :</span> {{ order.supplier.username }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634">Téléphone : </span>{{ order.supplier.phone }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634">Adresse :</span>
                    {{ order.supplier.address }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634"> Matricule Fiscale :</span>
                    {{ order.supplier.tax_identification_number }}
                  </b-card-text>
                </div>
              </div>
            </div>

            <!-- Invoice Description: Table -->
            <div class="mr-1 ml-1">
              <b-table-lite
                class="ta"
                style="font-size: 20px; border: 2px solid black"
                responsive
                :items="getOrderProducts(order)"
                :fields="['désignation', 'quantité']"
              >
                <template #cell(taskDescription)="data">
                  <b-card-text class="font-weight-bold mb-25">
                    {{ data.item.taskTitle }}
                  </b-card-text>
                  <b-card-text class="text-nowrap">
                    {{ data.item.taskDescription }}
                  </b-card-text>
                </template>
              </b-table-lite>
            </div>
            <!-- Invoice Description: Total -->
            <div class="mr-1 ml-1">
              <b-row>
                <b-col>
                  <h1 style="color: #0A2634; font-size: 18px ; font-weight: bold">
                    Commentaire :
                    <span style="font-size: 15px ; color:black;">
                      {{ order.note }}
                    </span>
                  </h1>
                  <div>
                    <b-card-text
                      v-if="order.is_fragile ==true"
                      class="h4"
                      style="color:black"
                    >
                      <strong style="color:#0A2634">Fragile :</strong> OUI
                    </b-card-text>
                    <b-card-text
                      v-if="order.is_discharge == true"
                      class="h4"
                      style="color:black"
                    >
                      <strong style="color:#0A2634">Décharge :</strong> OUI
                    </b-card-text>
                  </div>
                </b-col>
                <b-col
                  class="ml-2"
                  style="font-size: 20px"
                >
                  <table style="border: 2px solid #0A2634">
                    <tr class="badge-glow bg-info bg-lighten-5">
                      <th class="text-center">
                        Montant HT: {{ order.price_ht }}
                      </th>
                    </tr>
                    <tr class="badge-glow bg-primary bg-lighten-5">
                      <th class="text-center">
                        TVA 7 %: {{ order.tax }}
                      </th>
                    </tr>
                    <tr class="badge-glow bg-success bg-lighten-1">
                      <th class="text-center">
                        Total TTC: {{ order.price_ttc }} DT
                      </th>
                    </tr>
                  </table>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTableLite,
  BCardText,
  BButton,
  BAlert,
} from 'bootstrap-vue'
import VueBarcode from 'vue-barcode'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'
import storeAuth from '@/store/store'
import QrcodeVue from 'qrcode.vue'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BTableLite,
    BCardText,
    BButton,
    BAlert,
    barcode: VueBarcode,
    QrcodeVue,
  },
  data() {
    return {
      size: 80,
      orders: [],
      isLoading: false,
      company: {},
    }
  },
  mounted() {
    this.company = storeAuth.state.company
    this.loadOrders()
  },
  methods: {
    currentDateTime() {
      const current = new Date()
      const date = `${current.getFullYear()}-${current.getMonth() + 1}-${current.getDate()}`
      const time = `${current.getHours()}:${current.getMinutes()}:${current.getSeconds()}`
      const dateTime = `${date} | ${time}`

      return dateTime
    },
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(
        2,
        '0',
      )} | ${h}`
    },
    getOrderProducts(order) {
      const products = []
      if (order && order.products) {
        for (let i = 0; i < order.products.length; i += 1) {
          products.push({
            désignation: order.products[i].product,
            quantité: order.products[i].quantity,
          })
        }
      }
      return products
    },
    async loadOrders() {
      try {
        this.isLoading = true

        // Get order IDs from URL query parameter
        const orderIds = this.$route.query.ids

        if (!orderIds) {
          this.showToast('danger', 'Aucun échange spécifié')
          this.isLoading = false
          return
        }

        // Parse IDs (can be comma-separated string or array)
        const ids = Array.isArray(orderIds) ? orderIds : orderIds.split(',')

        // Call bulk delivery notes API to get all order data
        const { data } = await axios.post('/api/orders/bulk-delivery-notes/', {
          order_ids: ids,
        })

        // Backend returns {orders: [...], total_count: N, warning: '...'}
        this.orders = data.orders || data

        // Show warning if some orders were filtered out
        if (data.warning && data.filtered_count > 0) {
          this.showToast('warning', `${data.filtered_count} colis ont été exclus (non livrés ou non échangés)`)
        }

        // If no orders were returned, check if we have a payment ID to redirect to
        if (this.orders.length === 0) {
          const paymentId = this.$route.query.payment_id
          if (paymentId) {
            // Payment exists but no exchange orders, redirect to payment details
            this.showToast('info', 'Aucun colis d\'échange trouvé pour ce paiement. Redirection vers les détails du paiement...')
            setTimeout(() => {
              this.$router.push({ name: 'payment-show', params: { id: paymentId } })
            }, 1500)
          } else {
            // No payment ID either, just show message
            this.showToast('info', 'Aucun colis d\'échange livré trouvé')
          }
        }

        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.showToast('danger', 'Erreur lors du chargement des échanges')
        console.error('Error loading orders:', error)
      }
    },
    printAll() {
      // Trigger browser print
      window.print()
    },
    showToast(variant, text) {
      let icon = 'AlertCircleIcon'
      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else if (variant === 'warning') {
        icon = 'AlertTriangleIcon'
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon,
          text,
          variant,
        },
      }, {
        position: 'top-right',
        timeout: 5000,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
table,
td,
th {
  padding: 10px;
  text-align: left;
  border: 1px solid #0A2634;
}

table {
  width: 100%;
  border-collapse: collapse;
}
.mycolor {
  background-color: #EA2F2F;
}
.contact {
  background-color: #EA2F2F;
  padding: 10px;
  border-radius: 20px;
}

.order-section {
  margin-bottom: 2rem;
}
</style>

<style lang="scss">
@media print {
  .ta {
    white-space: nowrap;
    overflow-x: visible;
    font-size: 16px; // Slightly smaller than original 22px
    color: black;
  }

  footer {
    display: none;
  }

  .table thead th {
    border-bottom: 2px solid black;
  }

  body {
    background-color: transparent !important;
  }

  nav.header-navbar {
    display: none;
  }

  .main-menu {
    display: none;
  }

  .header-navbar-shadow {
    display: none !important;
  }

  .content.app-content {
    margin-left: 0;
    padding-top: 0.5rem !important; // Reduced from 2rem
  }

  .mycolor {
    background-color: #EA2F2F;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .contact {
    background-color: #EA2F2F;
    padding: 20px;
    border-radius: 25px;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  footer footer-light footer-static {
    display: inline-block;
  }

  footer.footer {
    display: none;
  }

  .card {
    background-color: transparent;
    box-shadow: none;
  }

  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
            > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }

  table,
  td,
  th {
    padding: 6px; // Reduced from 10px
    text-align: left;
    border: 1px solid black;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  // Bulk print specific styles
  .order-section {
    page-break-inside: avoid;
    margin-bottom: 1rem;
  }

  .page-break {
    page-break-after: always;
  }

  .no-print,
  button {
    display: none !important;
  }

  // Add separator between orders on same page
  .order-section:not(.page-break) {
    border-bottom: 2px dashed #ccc;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }

  // Remove border from last order on page (before page break)
  .order-section.page-break {
    border-bottom: none;
  }

  // Slightly reduce font sizes to fit 2 per page
  h3 {
    font-size: 0.9rem;
  }

  strong[style*="font-size: 28px"], strong[style*="font-size: 40px"] {
    font-size: 1.5rem !important;
  }

  .h4, .b-card-text {
    font-size: 0.85rem;
  }
}
</style>
